/**
 * DatePicker Component
 *
 * Native date input with consistent styling.
 * For complex date picking needs, consider using a library like react-datepicker.
 */

import { forwardRef, type InputHTMLAttributes } from 'react';
import { CalendarIcon } from '@heroicons/react/24/outline';

import { cn } from '../../utils/cn';

export interface DatePickerProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  /** Whether the input has an error state */
  error?: boolean | string;
  /** Label text */
  label?: string;
  /** Helper text */
  helpText?: string;
  /** Input type - 'date', 'datetime-local', 'time' */
  type?: 'date' | 'datetime-local' | 'time';
}

export const DatePicker = forwardRef<HTMLInputElement, DatePickerProps>(
  ({ className, error, type = 'date', label, helpText, id, ...props }, ref) => {
    const inputId = id || (label ? `datepicker-${Math.random().toString(36).substr(2, 9)}` : undefined);
    const hasError = Boolean(error);

    return (
      <div className="w-full">
        {label && (
          <label htmlFor={inputId} className="mb-1 block text-sm font-medium text-mv-gray-700">
            {label}
          </label>
        )}
        <div className="relative">
          <input
            id={inputId}
            type={type}
            ref={ref}
            className={cn(
              'flex h-10 w-full rounded border bg-white px-3 py-2 pr-10 text-sm',
              'placeholder:text-mv-gray-400',
              'focus:border-mv-blue-500 focus:outline-none focus:ring-2 focus:ring-mv-blue-500/20',
              'disabled:cursor-not-allowed disabled:bg-mv-gray-50 disabled:opacity-50',
              // Hide the default calendar icon on webkit browsers
              '[&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-0 [&::-webkit-calendar-picker-indicator]:w-10 [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer',
              hasError
                ? 'border-mv-red-600 focus:border-mv-red-600 focus:ring-mv-red-600/20'
                : 'border-mv-gray-300',
              className
            )}
            {...props}
          />
          <CalendarIcon
            className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-mv-gray-500"
            aria-hidden="true"
          />
        </div>
        {error && typeof error === 'string' && <p className="mt-1 text-xs text-mv-red-600">{error}</p>}
        {helpText && !error && <p className="mt-1 text-xs text-mv-gray-500">{helpText}</p>}
      </div>
    );
  }
);

DatePicker.displayName = 'DatePicker';

// Helper to format dates for the input
export function formatDateForInput(date: Date | string | undefined): string {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toISOString().split('T')[0];
}

// Helper to format datetime for the input
export function formatDateTimeForInput(date: Date | string | undefined): string {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toISOString().slice(0, 16);
}
